import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  CreditCard, 
  Phone, 
  MessageSquare, 
  CheckCircle, 
  ArrowRight, 
  Wallet, 
  Mail, 
  PieChart,
  UserCheck,
  Smartphone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const KiannaPatientBillingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Kianna, Patient Billing Agent</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet Kianna, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> AI Patient Billing Agent</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Kianna automates the entire patient billing process. She generates clear statements and runs intelligent SMS & voice campaigns to collect payments 24/7.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="relative w-full max-w-lg">
                    {/* Mobile Interface */}
                    <div className="bg-slate-900 rounded-[3rem] p-4 shadow-2xl border-4 border-slate-800 relative z-10 w-80 mx-auto">
                        <div className="bg-white rounded-[2.5rem] overflow-hidden h-[500px] relative">
                            {/* Header */}
                            <div className="bg-slate-50 p-6 pt-12 pb-4 border-b border-slate-100 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                    AD
                                </div>
                                <div>
                                    <div className="font-bold text-slate-800">Asha Dental</div>
                                    <div className="text-xs text-slate-500">Secure Billing</div>
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="p-4 space-y-4">
                                <div className="bg-slate-100 p-3 rounded-2xl rounded-tl-none text-sm text-slate-700 max-w-[90%]">
                                    Hi Michael, your insurance has processed your recent visit. Your remaining balance is <strong>$45.00</strong>.
                                </div>
                                
                                <div className="bg-white border border-blue-100 shadow-lg p-4 rounded-xl">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-xs font-bold text-slate-500">AMOUNT DUE</span>
                                        <span className="text-lg font-bold text-slate-900">$45.00</span>
                                    </div>
                                    <Button className="w-full bg-black hover:bg-slate-800 text-white rounded-lg h-10 text-sm font-medium flex items-center justify-center gap-2">
                                        <CreditCard className="w-4 h-4" />
                                        Pay with Apple Pay
                                    </Button>
                                </div>

                                <div className="flex justify-center py-2">
                                    <div className="text-[10px] text-slate-400 font-medium">Today 2:14 PM</div>
                                </div>

                                <div className="bg-blue-600 p-3 rounded-2xl rounded-tr-none text-sm text-white max-w-[90%] ml-auto">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4" />
                                        Payment Successful!
                                    </div>
                                </div>
                            </div>

                             {/* Kianna Indicator */}
                             <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-slate-900/5 px-3 py-1.5 rounded-full">
                                 <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                 <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">Powered by Kianna</span>
                             </div>
                        </div>
                    </div>

                    {/* Floating Stats */}
                    <div className="absolute -right-4 top-24 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-float">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <Wallet className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Collection Rate</div>
                                <div className="text-lg font-bold text-slate-900">94%</div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute -left-12 bottom-32 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-float" style={{ animationDelay: '1s' }}>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Smartphone className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Mobile Payments</div>
                                <div className="text-lg font-bold text-slate-900">68%</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Modern Payment Collection
            </h2>
            <p className="text-lg text-gray-600">
              Stop mailing paper statements that get ignored. Meet patients where they are—on their phones.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <MessageSquare className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Text-to-Pay</h3>
              <p className="text-gray-600">
                Sends a secure link via SMS. Patients can view their balance and pay in 2 clicks with Apple Pay or Google Pay.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <Phone className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Voice Outreach</h3>
              <p className="text-gray-600">
                For larger balances, Kianna calls patients to discuss payment options or set up payment plans politely.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <Wallet className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Ledger Sync</h3>
              <p className="text-gray-600">
                Every payment collected is instantly posted to the Asha PMS ledger, closing out the balance automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <CreditCard className="w-4 h-4" />
                The Collection Cycle
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How Kianna Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                She works the moment a balance becomes patient responsibility, ensuring you get paid faster.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Detects Balance</h4>
                    <p className="text-slate-400 text-sm">Identifies patient responsibility after insurance pays.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Generates Statement</h4>
                    <p className="text-slate-400 text-sm">Creates a clear, itemized digital statement explaining the charge.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Sends Campaign</h4>
                    <p className="text-slate-400 text-sm">Delivers via SMS/Email first, then escalates to voice calls if needed.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Processes Payment</h4>
                    <p className="text-slate-400 text-sm">Securely takes payment and updates your practice software.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                              <MessageSquare className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Payment Link Sent</div>
                              <div className="text-xs text-slate-400">To: Sarah J.</div>
                          </div>
                      </div>
                      <div className="text-xs font-mono text-slate-500">Delivered</div>
                  </div>

                  <div className="space-y-4">
                      <div className="bg-slate-700 p-4 rounded-2xl rounded-tl-none text-sm text-slate-200 max-w-[85%]">
                          <p className="mb-2">Hi Sarah, this is a secure billing message from Dr. Smith's office.</p>
                          <p className="mb-3">Your insurance has processed your recent visit. The remaining balance is <strong>$45.00</strong>.</p>
                          <div className="bg-slate-800 p-3 rounded-xl border border-slate-600 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                  <div className="bg-green-500/20 p-1.5 rounded-lg">
                                      <CreditCard className="w-4 h-4 text-green-400" />
                                  </div>
                                  <span className="font-bold text-white">Pay $45.00</span>
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                          </div>
                      </div>
                      
                      <div className="flex justify-center">
                          <div className="bg-green-500/10 text-green-400 text-xs px-3 py-1 rounded-full border border-green-500/20 flex items-center gap-2">
                              <CheckCircle className="w-3 h-3" />
                              Payment Received - 2 mins later
                          </div>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">45%</div>
              <div className="text-gray-600 font-medium">Faster Collections</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">30%</div>
              <div className="text-gray-600 font-medium">Reduction in Paper Costs</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">24/7</div>
              <div className="text-gray-600 font-medium">Payment Portal Uptime</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Get paid without the chase.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Let Kianna handle your patient billing so you can focus on dentistry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Automate Billing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default KiannaPatientBillingPage;
